/**
 * useProfitability Hook
 *
 * Fetches profitability data by strain and customer segment for a given period.
 * Uses the profitability-analysis Edge Function for calculations.
 *
 * Part of US-P7-002: Profitability Analysis
 *
 * @example
 * const {
 *   strainData,
 *   segmentData,
 *   summary,
 *   isLoading,
 *   error,
 *   refetch
 * } = useProfitability({
 *   periodStart: '2025-01-01',
 *   periodEnd: '2025-12-31'
 * })
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'

// =============================================================================
// Constants
// =============================================================================

const FUNCTION_NAME = 'profitability-analysis'

// =============================================================================
// Edge Function Helper
// =============================================================================

/**
 * Call the profitability-analysis Edge Function
 *
 * @param {string} path - The endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 * @throws {Error} On network or API error
 */
async function callProfitabilityFunction(path, options = {}) {
  const { params } = options

  let fullPath = `${FUNCTION_NAME}/${path}`
  if (params) {
    fullPath += `?${params.toString()}`
  }

  const { data, error } = await supabase.functions.invoke(fullPath, { method: 'GET' })

  if (error) {
    throw new Error(error.message || 'Edge function error')
  }

  // Check for API-level errors in response
  if (data?.error) {
    const err = new Error(data.message || data.error)
    err.retryable = data.retryable ?? false
    throw err
  }

  return data
}

// =============================================================================
// Main Hook: useProfitability
// =============================================================================

/**
 * Hook to fetch profitability data by strain and segment
 *
 * @param {Object} options - Hook options
 * @param {string} options.periodStart - Start date in YYYY-MM-DD format
 * @param {string} options.periodEnd - End date in YYYY-MM-DD format
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} Hook state and actions
 */
export function useProfitability({ periodStart, periodEnd, enabled = true }) {
  const [strainData, setStrainData] = useState(null)
  const [segmentData, setSegmentData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Build query params
  const params = useMemo(() => {
    if (!periodStart || !periodEnd) return null
    const searchParams = new URLSearchParams()
    searchParams.set('period_start', periodStart)
    searchParams.set('period_end', periodEnd)
    return searchParams
  }, [periodStart, periodEnd])

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!params || !enabled) return

    setIsLoading(true)
    setError(null)

    try {
      // Fetch strain and segment data in parallel
      const [strainResponse, segmentResponse] = await Promise.all([
        callProfitabilityFunction('strain', { params }),
        callProfitabilityFunction('segment', { params })
      ])

      setStrainData(strainResponse)
      setSegmentData(segmentResponse)
    } catch (err) {
      console.error('[useProfitability] Error fetching data:', err)
      setError(err.message || 'Failed to fetch profitability data')
    } finally {
      setIsLoading(false)
    }
  }, [params, enabled])

  // Fetch on mount and when params change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Calculate summary metrics
  const summary = useMemo(() => {
    if (!strainData || !segmentData) return null

    const strains = strainData.strains || []
    const segments = segmentData.segments || []

    // Aggregate from strain data (more detailed breakdown)
    const totalRevenue = strains.reduce((sum, s) => sum + (s.revenue || 0), 0)
    const totalKgSold = strains.reduce((sum, s) => sum + (s.kg_sold || 0), 0)
    const totalSaleCount = strains.reduce((sum, s) => sum + (s.sale_count || 0), 0)
    const totalVariableCost = strains.reduce((sum, s) => sum + (s.total_variable_cost || 0), 0)
    const totalContributionMargin = strains.reduce((sum, s) => sum + (s.contribution_margin || 0), 0)

    const avgMarginPercent = totalRevenue > 0
      ? (totalContributionMargin / totalRevenue) * 100
      : 0

    return {
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalKgSold: Math.round(totalKgSold * 1000) / 1000,
      totalSaleCount,
      variableCostPerKg: strainData.variable_cost_per_kg || 0,
      totalVariableCost: Math.round(totalVariableCost * 100) / 100,
      totalContributionMargin: Math.round(totalContributionMargin * 100) / 100,
      avgMarginPercent: Math.round(avgMarginPercent * 10) / 10,
      strainCount: strains.length,
      segmentCount: segments.length
    }
  }, [strainData, segmentData])

  return {
    // Data
    strainData,
    segmentData,
    summary,

    // Convenience accessors
    strains: strainData?.strains || [],
    segments: segmentData?.segments || [],
    variableCostPerKg: strainData?.variable_cost_per_kg || 0,

    // Period info
    periodStart: strainData?.period_start || periodStart,
    periodEnd: strainData?.period_end || periodEnd,

    // Status
    isLoading,
    error,

    // Actions
    refetch: fetchData
  }
}

// =============================================================================
// Helper Hook: useStrainProfitability
// =============================================================================

/**
 * Hook to fetch drill-down detail for a specific strain
 *
 * @param {Object} options - Hook options
 * @param {string} options.strainName - Name of the strain
 * @param {string} options.periodStart - Start date in YYYY-MM-DD format
 * @param {string} options.periodEnd - End date in YYYY-MM-DD format
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} Hook state and actions
 */
export function useStrainProfitability({ strainName, periodStart, periodEnd, enabled = true }) {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Build query params
  const params = useMemo(() => {
    if (!periodStart || !periodEnd) return null
    const searchParams = new URLSearchParams()
    searchParams.set('period_start', periodStart)
    searchParams.set('period_end', periodEnd)
    return searchParams
  }, [periodStart, periodEnd])

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!strainName || !params || !enabled) return

    setIsLoading(true)
    setError(null)

    try {
      const encodedName = encodeURIComponent(strainName)
      const response = await callProfitabilityFunction(`strain/${encodedName}`, { params })
      setData(response)
    } catch (err) {
      console.error('[useStrainProfitability] Error fetching data:', err)
      setError(err.message || 'Failed to fetch strain profitability')
    } finally {
      setIsLoading(false)
    }
  }, [strainName, params, enabled])

  // Fetch on mount and when params change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    strain: data?.strain_name || strainName,
    summary: data?.summary || null,
    sales: data?.sales || [],
    isLoading,
    error,
    refetch: fetchData
  }
}

// =============================================================================
// Helper Hook: useSegmentProfitability
// =============================================================================

/**
 * Hook to fetch drill-down detail for a specific customer segment
 *
 * @param {Object} options - Hook options
 * @param {string} options.segmentName - Name of the segment
 * @param {string} options.periodStart - Start date in YYYY-MM-DD format
 * @param {string} options.periodEnd - End date in YYYY-MM-DD format
 * @param {boolean} [options.enabled=true] - Whether to fetch data
 * @returns {Object} Hook state and actions
 */
export function useSegmentProfitability({ segmentName, periodStart, periodEnd, enabled = true }) {
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Build query params
  const params = useMemo(() => {
    if (!periodStart || !periodEnd) return null
    const searchParams = new URLSearchParams()
    searchParams.set('period_start', periodStart)
    searchParams.set('period_end', periodEnd)
    return searchParams
  }, [periodStart, periodEnd])

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!segmentName || !params || !enabled) return

    setIsLoading(true)
    setError(null)

    try {
      const encodedName = encodeURIComponent(segmentName)
      const response = await callProfitabilityFunction(`segment/${encodedName}`, { params })
      setData(response)
    } catch (err) {
      console.error('[useSegmentProfitability] Error fetching data:', err)
      setError(err.message || 'Failed to fetch segment profitability')
    } finally {
      setIsLoading(false)
    }
  }, [segmentName, params, enabled])

  // Fetch on mount and when params change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    segment: data?.segment_name || segmentName,
    segmentId: data?.segment_id || null,
    summary: data?.summary || null,
    customers: data?.customers || [],
    isLoading,
    error,
    refetch: fetchData
  }
}

// =============================================================================
// Period Helper Hooks
// =============================================================================

/**
 * Get period dates based on a preset type
 *
 * @param {string} periodType - 'month' | 'quarter' | 'ytd' | 'trailing12'
 * @param {Date} [baseDate=new Date()] - Base date for calculations
 * @returns {{ periodStart: string, periodEnd: string, label: string }}
 */
export function usePeriodDates(periodType, baseDate = new Date()) {
  return useMemo(() => {
    const today = baseDate
    const year = today.getFullYear()
    const month = today.getMonth()

    let periodStart, periodEnd, label

    switch (periodType) {
      case 'month': {
        // Current month
        periodStart = new Date(year, month, 1)
        periodEnd = new Date(year, month + 1, 0) // Last day of month
        label = periodStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
        break
      }
      case 'quarter': {
        // Current quarter
        const quarterMonth = Math.floor(month / 3) * 3
        periodStart = new Date(year, quarterMonth, 1)
        periodEnd = new Date(year, quarterMonth + 3, 0)
        const quarter = Math.floor(month / 3) + 1
        label = `Q${quarter} ${year}`
        break
      }
      case 'ytd': {
        // Year to date
        periodStart = new Date(year, 0, 1)
        periodEnd = today
        label = `YTD ${year}`
        break
      }
      case 'trailing12': {
        // Trailing 12 months
        periodStart = new Date(year - 1, month + 1, 1)
        periodEnd = new Date(year, month + 1, 0)
        label = 'Trailing 12 Months'
        break
      }
      default: {
        // Default to current month
        periodStart = new Date(year, month, 1)
        periodEnd = new Date(year, month + 1, 0)
        label = periodStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
      }
    }

    const formatDate = (date) => {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }

    return {
      periodStart: formatDate(periodStart),
      periodEnd: formatDate(periodEnd),
      label
    }
  }, [periodType, baseDate])
}

// =============================================================================
// Default Export
// =============================================================================

export default useProfitability
